import { Rect } from '../../../platform/geom/Rect';
import { TextAlignment, TextSettings } from '../../canvas/entities/TextConventions';
import { TextRuler } from '../../canvas/visualization/TextRuler';
import { CommentCountAttr, CommentOpenAttr } from '../annotations/CommentAssociations';
import { Tag } from '../annotations/Tag';
import { TagLayout, TagLike } from '../annotations/TagLayout';
import { VoteCountAttr } from '../voting/Vote';
import { TaskCard } from './TaskCard';

const titleLayout = (object: TaskCard, base: Rect) => {

    const settings = TextSettings.from({
        fontColor: '#555',
        fontFamily: 'Cabin',
        fontSize: 22,
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: 1.25,
        maxFontSize: 0,
        textMargin: [10, 5, 5, 20],
        textAlignH: TextAlignment.start,
        textAlignV: TextAlignment.start,
    });

    const height = TextRuler.measureHeight(object.title, settings, base.width);
    const bounds = Rect.fromEdges(0, 0, base.width, height);

    return Object.freeze({ bounds, settings });
};

const contentLayout = (card: TaskCard, base: Rect, titleHeight: number, metaHeight: number) => {

    const baseSettings = TextSettings.from({
        fontColor: '#555',
        fontFamily: 'Cabin',
        fontSize: 0,
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: 1.25,
        maxFontSize: 18,
        textMargin: [5, 5, Math.max(5, metaHeight + 5), 20],
        textAlignH: TextAlignment.start,
        textAlignV: TextAlignment.start,
    });

    const bounds = Rect.fromEdges(0, titleHeight, base.width, base.height);

    const fontSize = Math.min(
        baseSettings.maxFontSize,
        TextRuler.fit(card.content, baseSettings, bounds.width, bounds.height),
    );

    const settings = { ...baseSettings, fontSize };

    return Object.freeze({ bounds, settings });
};

export const TaskCardLayout = (card: TaskCard) => {
    const { width, height } = card;

    const base = new Rect(0, 0, width, height);
    const badges = [] as TagLike[];

    if (CommentCountAttr.get(card) > 0 || CommentOpenAttr.get(card)) {
        badges.push({
            id: 'comments',
            text: (CommentCountAttr.get(card) || 0).toString(),
            icon: true,
        });
    }

    if (VoteCountAttr.get(card) > 0) {
        badges.push({
            id: 'votes',
            text: `+${VoteCountAttr.get(card)}`,
        });
    }

    const tags = TagLayout([...badges, ...Tag.sort(card.polarize(Tag))], base);


    const title = titleLayout(card, base);
    const content = contentLayout(card, base, title.bounds.height, tags.all.height);

    return Object.freeze({ width, height, title, content, tags });
};

export type TaskCardLayout = ReturnType<typeof TaskCardLayout>;
